import { useMemo } from "react";
import {
  extractPendingDepositBonusClaim,
  type PromotionClaim,
} from "types/PromotionTypes";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { currentAutomationProgression } from "@/hooks/firestore/models/currentAutomationProgression";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";

const usePromotionClaims = () => {
  const currency = useFiatCurrency();

  const { data, isLoading } = useFirestore(
    "/users/{userId}/currentAutomationProgressions",
    currentAutomationProgression.array(),
  );

  const claims: PromotionClaim[] = useMemo(() => {
    if (!data) return [];

    return (
      data
        // exclude progressions that are not deposit matches
        .filter((progression) => {
          return (
            (progression?.actions || []).some((action) => {
              return (
                action.type === "DEPOSIT_MATCH" &&
                action.status === "UNACTIONED"
              );
            }) &&
            (progression?.requirements || []).some((requirement) => {
              return requirement.type === "DEPOSIT";
            })
          );
        })
        // convert to promotion claim type
        .map((progression) => {
          const sortedActions =
            progression.actions?.sort((a, b) => {
              // sort by highest Max value
              const maxA = a?.projectedRewardInfo?.max || 0;
              const maxB = b?.projectedRewardInfo?.max || 0;
              return maxB - maxA;
            }) || [];

          const maxProjectedRewardInfo = sortedActions[0]?.projectedRewardInfo;

          const isCumulative =
            sortedActions.some((action) => action.isCumulative) || false;

          const unactionedProjectedRewardInfo = sortedActions.find(
            (action) => action.status === "UNACTIONED",
          )?.projectedRewardInfo;

          const amount = maxProjectedRewardInfo?.max || 0;
          const amountRemaining = unactionedProjectedRewardInfo?.max || 0;
          const amountClaimed = amount - amountRemaining;

          return {
            code: progression.progressionId,
            expireAt: progression.expiresAt?.toDate(),
            amount,
            currency,
            name: progression.name,
            matchPercentage: maxProjectedRewardInfo?.percent || 0,
            purpose: "DEPOSIT_BONUS", // for now, we are mapping to legacy until we can remove all legacy claims logic
            paidStatus: "PENDING", // for now, we are mapping to legacy until we can remove all legacy claims logic
            isCumulative,
            amountClaimed,
            amountRemaining,
          } satisfies PromotionClaim;
        })
    );
  }, [data, currency]);

  return { claims, isLoading };
};

export const usePendingDepositClaim = () => {
  const { claims, isLoading: claimsLoading } = usePromotionClaims();
  const pendingDepositClaim = extractPendingDepositBonusClaim(claims);

  return { pendingDepositClaim, isLoading: claimsLoading } as const;
};

type PromotionClaims = Exclude<
  ReturnType<typeof usePromotionClaims>["claims"],
  undefined
>;

export { usePromotionClaims, type PromotionClaims };
